import { Check, Circle, RefreshCw, AlertTriangle, Unlink, Loader2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SyncStatus } from "@/api/syncApi";

const CHIP: Record<SyncStatus, { label: string; className: string; Icon: typeof Check; withKey: boolean; iconSize: number; iconStroke: number }> = {
    NOT_READY:      { label: "Not ready",    className: "bg-gray-50 text-gray-400 border-gray-200",          Icon: Circle,        withKey: false, iconSize: 11, iconStroke: 2 },
    SYNCED:         { label: "Synced",       className: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check,         withKey: true,  iconSize: 12, iconStroke: 3 },
    NOT_SYNCED:     { label: "Not synced",   className: "bg-gray-100 text-gray-600 border-gray-200",         Icon: Circle,        withKey: false, iconSize: 11, iconStroke: 2 },
    LOCAL_CHANGED:  { label: "Push ready",   className: "bg-amber-50 text-amber-700 border-amber-200",       Icon: RefreshCw,     withKey: true,  iconSize: 12, iconStroke: 2 },
    REMOTE_CHANGED: { label: "Update ready", className: "bg-amber-50 text-amber-700 border-amber-200",       Icon: RefreshCw,     withKey: true,  iconSize: 12, iconStroke: 2 },
    CONFLICT:       { label: "Conflict",     className: "bg-rose-50 text-rose-700 border-rose-200",          Icon: AlertTriangle, withKey: true,  iconSize: 12, iconStroke: 2 },
    ORPHANED:       { label: "Deleted remotely", className: "bg-gray-100 text-gray-500 border-gray-200",     Icon: Unlink,        withKey: true,  iconSize: 12, iconStroke: 2 },
};

// 1B pill metrics shared by every variant so the status column stays aligned
// (see docs/design/design_handoff_ticket_link_1b). Labels are uppercased via
// CSS; ticket keys opt back out through KEY_CLASS below.
const BASE =
    "flex h-[30px] items-center justify-center gap-[7px] whitespace-nowrap rounded-full border text-[11px] font-extrabold uppercase tracking-[0.06em]";

// Explicit font stack rather than Tailwind's font-mono: its --font-mono var is
// only defined by screen-scoped tokens.css, which not every SyncPanel host loads.
const KEY_CLASS =
    "[font-family:'JetBrains_Mono',ui-monospace,'SF_Mono',Menlo,monospace] text-[11.5px] normal-case tracking-normal";

export interface StatusChipProps {
    status: SyncStatus;
    externalKey?: string | null;
    /** When set (SYNCED only), the chip renders as a new-tab link to the ticket. */
    href?: string;
    /** Provider name for the link's accessible label, e.g. "Jira", "Azure DevOps". */
    vendorLabel?: string;
    inFlight?: boolean;
    step?: string;
    className?: string;
    /** Called when the SYNCED link-out chip is clicked (does not preventDefault — the new tab still opens). */
    onLinkOut?: () => void;
}

export function StatusChip({ status, externalKey, href, vendorLabel, inFlight, step, className, onLinkOut }: StatusChipProps) {
    if (inFlight) {
        return (
            <span className={cn(BASE, "border-[#f7f9ff] bg-[#f7f9ff] text-[#103699]", className)}>
                <Loader2 size={12} className="shrink-0 animate-spin" />
                {step === "pulling" ? "Updating…" : step === "resolving" ? "Resolving…" : "Syncing…"}
            </span>
        );
    }
    const chip = CHIP[status];
    const label = chip.withKey && externalKey ? (
        <>
            {chip.label} · <span className={KEY_CLASS}>{externalKey}</span>
        </>
    ) : (
        chip.label
    );
    if (href && status === "SYNCED") {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${externalKey} in ${vendorLabel}`}
                onClick={onLinkOut}
                className={cn(
                    BASE,
                    chip.className,
                    "no-underline transition-colors duration-150 hover:border-emerald-300 hover:bg-emerald-100 hover:no-underline",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1954ed]",
                    className
                )}
            >
                <chip.Icon size={chip.iconSize} strokeWidth={chip.iconStroke} className="shrink-0" />
                {label}
                <ExternalLink size={12} strokeWidth={2} aria-hidden="true" className="shrink-0 opacity-75" />
            </a>
        );
    }
    return (
        <span className={cn(BASE, chip.className, className)}>
            <chip.Icon size={chip.iconSize} strokeWidth={chip.iconStroke} className="shrink-0" />
            {label}
        </span>
    );
}
